import React from 'react';
import {ActivityIndicator, Platform, StyleSheet, TouchableOpacity, View} from 'react-native';
import Animated, {FadeIn, FadeOut} from 'react-native-reanimated';
import {makeStyles} from '@rn-vui/themed';
import {observer} from 'mobx-react-lite';

import CartButton from '../../../../components/CartButton';
import {
  MembersPriceLabel,
  ProductTileImage,
  ProductTileInfo,
  useSecondaryCalloutLabels,
} from '../../../../components/ProductTile';
import {COLORS, TILE_DIMENSIONS, TYPOGRAPHY} from '../../../../components/ProductTile/styles';
import Text from '../../../../components/ui-controls/Text';
import {useResolvedPricing} from '../../../../hooks/pricing';
import {AppRoute} from '../../../../models/AppRoute';
import useAppNavigation from '../../../../navigation/useAppNavigation';
import {useStores} from '../../../../stores/use-stores';
import theme from '../../../../style/theme';
import {OrderLine} from '../../../../transport/apiServiceResponse';
import {toAUD} from '../../../../utils/functions';
import {buildProductTestId} from '../../../../utils/string';

type CartLineItemProps = {
  item: OrderLine;
  index: number;
  disabled?: boolean;
  isTotalVisible?: boolean;
  isGiftCardStore?: boolean;
};

const MEMBER_SIDEBAR_TEXT = {
  public: 'MEMBERS PRICE',
  private: 'JUST FOR YOU',
} as const;

const CartLineItem = observer(
  ({
    item,
    index: _index,
    disabled = false,
    isTotalVisible = true,
    isGiftCardStore = false,
  }: CartLineItemProps) => {
    const styles = useStyles();
    const navigation = useAppNavigation();
    const {cartStore} = useStores();

    if (!item) return null;

    const {originalPriceLabel} = useSecondaryCalloutLabels(item, {
      originalPriceLabel: styles.originalPrice,
    });
    const pricing = useResolvedPricing(item, 'transactional');
    const testID = buildProductTestId(item.name, 'cart');

    // DIS-1590: the sidebar label and the member / non-member pair are adverts — they
    // describe the product at this store, not this shopper's entitlement. The charged
    // figure is the line Total below, which comes straight from `subtotal`.
    const hasMemberPricing =
      pricing.advertiseMemberPrice && pricing.memberPrice !== null && !!pricing.membershipType;

    const onPress = () => {
      // React Navigation v7: Navigate to product detail
      // Must use ROOT → MAIN → PRODUCT hierarchy to support navigation
      // from both regular cart and checkout flow (ROOT_CHECKOUT_FLOW)
      navigation.navigate(AppRoute.ROOT, {
        screen: AppRoute.MAIN,
        params: {
          screen: AppRoute.PRODUCT,
          params: {product: item},
        },
      });
    };

    return (
      <Animated.View entering={FadeIn.duration(300)} exiting={FadeOut.duration(50)}>
        <TouchableOpacity
          onPress={() => onPress()}
          style={[styles.item, hasMemberPricing ? styles.itemMember : null]}
          disabled={disabled}
          testID={testID}>
          {/* Yellow left sidebar with rotated label — flush left, full card height */}
          {hasMemberPricing && pricing.membershipType ? (
            <View
              style={[
                styles.memberSidebar,
                pricing.membershipType === 'private' ? styles.memberSidebarPrivate : null,
              ]}
              testID={`${testID}-member-sidebar`}>
              <Text style={styles.memberSidebarText} numberOfLines={1}>
                {MEMBER_SIDEBAR_TEXT[pricing.membershipType]}
              </Text>
            </View>
          ) : null}

          {/* Card body: top product-info row + dedicated footer row below */}
          <View style={styles.cardBody}>
            <View style={styles.productInfoRow}>
              {/* Image column — full card height, vertically centred */}
              <View style={styles.imageContainer}>
                <ProductTileImage item={item} size="small" testID={`${testID}-image`} />
              </View>

              {/* Right column — name / byline / price */}
              <View style={styles.rightColumn}>
                {/* Fallback centred badge when no member sidebar applies */}
                {hasMemberPricing ? null : (
                  <MembersPriceLabel
                    showLabel={pricing.advertiseMemberPrice}
                    labelText={pricing.memberLabelText}
                    center
                  />
                )}

                <ProductTileInfo
                  item={item}
                  nameNumberOfLines={2}
                  testID={`${testID}-name`}
                  containerStyle={styles.infoContainer}
                />

                {hasMemberPricing ? (
                  <View style={styles.dualPriceRow}>
                    <View style={styles.priceSegment}>
                      <Text style={styles.priceTag}>Member </Text>
                      <Text style={styles.memberPrice} testID={`${testID}-member-price`}>
                        {toAUD(pricing.memberPrice!)}
                      </Text>
                      <Text style={styles.priceUnit}> each</Text>
                    </View>
                    {pricing.nonMemberPrice !== null ? (
                      <View style={styles.priceSegment}>
                        <Text style={styles.priceTag}>Non-members </Text>
                        <Text
                          style={[
                            styles.nonMemberPrice,
                            pricing.showMemberLabel ? styles.nonMemberPriceStruck : null,
                          ]}
                          testID={`${testID}-nonmember-price`}>
                          {toAUD(pricing.nonMemberPrice)}
                        </Text>
                        <Text style={styles.priceUnit}> each</Text>
                      </View>
                    ) : null}
                  </View>
                ) : (
                  <View style={styles.priceLine}>
                    <Text style={styles.price}>{toAUD(pricing.displayPrice)}</Text>
                    <Text style={styles.priceUnit}> ea</Text>
                  </View>
                )}

                {originalPriceLabel}
              </View>
            </View>

            {/* Dedicated footer row — full width below product info */}
            <View style={styles.footer}>
              {!!isTotalVisible && (
                <View style={styles.totalLine}>
                  <Text style={styles.totalText}>Total: </Text>
                  {!!cartStore.dirtyOrder && (
                    <ActivityIndicator color={COLORS.buttonPrimary} size="small" />
                  )}
                  {!!(!cartStore.dirtyOrder && item.subtotal) && (
                    <Text style={styles.totalPrice}>{toAUD(item.subtotal.cents_inc_tax)}</Text>
                  )}
                </View>
              )}
              {!isGiftCardStore && (
                <View style={styles.cartButtonWrapper}>
                  <CartButton item={item} testContext="cart" />
                </View>
              )}
            </View>
          </View>
        </TouchableOpacity>
      </Animated.View>
    );
  },
);

export default CartLineItem;

const MEMBER_YELLOW = theme.colour.background.memberPriceBadge;
const TILE_SURFACE = theme.colour.background.tile.surface;

const useStyles = makeStyles(_themeFromVui =>
  StyleSheet.create({
    // Outer card carries border + sizing only — no padding so sidebar can
    // stretch flush from top to bottom. Inner columns own their own padding.
    item: {
      borderWidth: TILE_DIMENSIONS.borderWidth,
      borderColor: COLORS.tileBorder,
      borderRadius: TILE_DIMENSIONS.borderRadius,
      backgroundColor: TILE_SURFACE,
      marginBottom: 6,
      flexDirection: 'row',
      alignItems: 'stretch',
      minHeight: TILE_DIMENSIONS.horizontalMinHeight,
      overflow: 'hidden',
    },
    // Yellow 2px border when member pricing applies — sidebar fills full height
    itemMember: {
      borderWidth: TILE_DIMENSIONS.memberLabelBorderWidth,
      borderColor: MEMBER_YELLOW,
    },
    // Flush-left yellow sidebar with rotated label, mirrors ProductTileHorizontal.
    // alignSelf:'stretch' + outer item padding:0 ⇒ sidebar reaches full card height.
    memberSidebar: {
      width: TILE_DIMENSIONS.horizontalMemberLabelHeight,
      backgroundColor: MEMBER_YELLOW,
      justifyContent: 'center',
      alignItems: 'center',
      alignSelf: 'stretch',
      overflow: 'hidden',
    },
    memberSidebarPrivate: {
      backgroundColor: 'transparent',
      borderRightWidth: TILE_DIMENSIONS.memberLabelBorderWidth,
      borderRightColor: MEMBER_YELLOW,
    },
    memberSidebarText: {
      fontSize: 8,
      fontWeight: 'bold',
      color: COLORS.nameText,
      letterSpacing: 1,
      textAlign: 'center',
      ...Platform.select({
        web: {
          // CSS writing-mode rotates text within the container flow,
          // unlike transform:rotate which doesn't change box model.
          writingMode: 'vertical-rl' as const,
          transform: [{rotate: '180deg'}],
          whiteSpace: 'nowrap' as const,
        },
        default: {
          // Native: explicit width prevents wrapping before rotation.
          // After -90deg, this width becomes the visual height.
          width: 100,
          transform: [{rotate: '-90deg'}],
        },
      }),
    },
    // CardBody = column wrapper to the right of the optional sidebar.
    // Holds productInfoRow (image + content) on top and footer row below.
    cardBody: {
      flex: 1,
      flexDirection: 'column',
    },
    // Product info row: image on the left, content on the right.
    productInfoRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    // Image column — vertically centred relative to the product info row.
    imageContainer: {
      width: 88,
      alignSelf: 'stretch',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 8,
      paddingLeft: 8,
    },
    rightColumn: {
      flex: 1,
      flexDirection: 'column',
      paddingVertical: 8,
      paddingLeft: TILE_DIMENSIONS.horizontalContentPaddingLeft,
      paddingRight: 8,
    },
    infoContainer: {
      marginBottom: 4,
    },
    priceLine: {
      flexDirection: 'row',
      alignItems: 'baseline',
      marginTop: 2,
    },
    // User preference: all price values render in black so the cart figure
    // stands out from the supporting labels. We use COLORS.nameText
    // (`theme.colour.text.tile.name` = LMG_BLACK on every brand) so the
    // colour stays brand-token-driven rather than literally hardcoded.
    price: {
      ...TYPOGRAPHY.priceHorizontal,
      color: COLORS.nameText,
    },
    priceUnit: {
      ...TYPOGRAPHY.priceUnit,
      color: COLORS.bylineText,
    },
    originalPrice: {
      ...TYPOGRAPHY.wasPrice,
      color: COLORS.nameText,
    },
    dualPriceRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      columnGap: TILE_DIMENSIONS.horizontalPriceSegmentGap,
      marginTop: 2,
    },
    priceSegment: {
      flexDirection: 'row',
      alignItems: 'baseline',
    },
    priceTag: {
      ...TYPOGRAPHY.priceUnit,
      color: COLORS.bylineText,
    },
    memberPrice: {
      ...TYPOGRAPHY.priceHorizontal,
      color: COLORS.nameText,
    },
    nonMemberPrice: {
      ...TYPOGRAPHY.priceHorizontal,
      color: COLORS.nameText,
    },
    // DIS-1708: see ProductTileHorizontal — the strike is a claim about the reader, so
    // it follows entitlement (`showMemberLabel`), not the store-scoped advert gate.
    nonMemberPriceStruck: {
      textDecorationLine: 'line-through',
    },
    // Dedicated footer row — full card width below productInfoRow.
    // Total + qty stepper grouped together, both right-aligned.
    footer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
      columnGap: 12,
      rowGap: 6,
      paddingVertical: 8,
      paddingHorizontal: 8,
    },
    totalLine: {
      flexDirection: 'row',
      alignItems: 'center',
      flexShrink: 0,
      minHeight: 30,
    },
    totalText: {
      ...TYPOGRAPHY.buttonLabel,
      color: COLORS.nameText,
    },
    totalPrice: {
      ...TYPOGRAPHY.priceHorizontal,
      color: COLORS.nameText,
    },
    cartButtonWrapper: {
      flexShrink: 0,
      minWidth: 110,
      maxWidth: 140,
    },
  }),
);
